import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';


import { SPECIAL_BARCODES } from '@constants/specialBarcodes';
import useQcFlowMachine from '@hooks/operator/qc/useQcFlowMachine';
import { useChunkedUploadQueue } from '@hooks/operator/useChunkedUploadQueue';
import { message, Modal } from 'antd';

import i18n from '@i18n/i18n';

import { operatorApiFetch } from '@services/operator/apiService';

import { QCFlowEvents, QCFlowStates } from './qcFlowMachine';
import { QcOperatorProvider, useQcOperator } from './QcOperatorContext';
import { QcParcelProvider, useQcParcel } from './QcParcelContext';
import { QcShipmentProvider, useQcShipping } from './QcShipmentContext';

const QcContext = createContext();

// ✅ 輔助函數：統一的 batch 比對邏輯
const normalizeBatch = (batch) => {
  if (batch === null || batch === undefined || batch === '') {
    return null;
  }
  return batch;
};

const isBatchMatch = (batch1, batch2) => {
  return normalizeBatch(batch1) === normalizeBatch(batch2);
};

// ✅ 內部組件：整合所有 Context
// ⚠️ 重要：接收來自 QcProvider 的狀態機 props,確保只有一個狀態機實例
const QcContextInner = ({ children, qcFlowMachine, externalPrintError, clearExternalPrintError }) => {
  // 使用三個新的 Context hooks
  const qcOperatorContext = useQcOperator();
  const qcParcelContext = useQcParcel();
  const qcShippingContext = useQcShipping();

  // ✅ 解構出 parcels，這樣 React 才能正確追蹤變化
  const { parcels, currentParcelIndex } = qcParcelContext;

  // 狀態管理
  const [currentQcFulfillment, setCurrentQcFulfillment] = useState(null);
  const [fulfillmentCandidates, setFulfillmentCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorInterpolations, setErrorInterpolations] = useState(null);

  // 同步來自 QcShipmentContext 的背景列印錯誤
  useEffect(() => {
    if (externalPrintError) {
      // 支援物件格式 { code, interpolations } 以帶入 i18n 插值；相容舊的純字串
      if (typeof externalPrintError === 'object') {
        setError(externalPrintError.code || 'shipment_failed');
        setErrorInterpolations(externalPrintError.interpolations || null);
      } else {
        setError(externalPrintError);
        setErrorInterpolations(null);
      }
      clearExternalPrintError();
    }
  }, [externalPrintError, clearExternalPrintError]);

  // 包裝鎖定衝突狀態
  const [packingLockConflict, setPackingLockConflict] = useState(null);

  // 平台訂單取消查詢失敗：進場前同步查平台狀態時 API 失敗，等待 operator 決定是否仍要進入 QC
  const [platformCheckFailed, setPlatformCheckFailed] = useState(null);

  // ✅ 使用從 props 傳入的狀態機(而不是創建新的)
  const {
    state: qcFlowState,
    previousState: qcFlowPreviousState,
    lastEvent: qcFlowLastEvent,
    context: qcFlowContext,
    label: qcFlowLabel,
    trigger: triggerQcFlowEvent,
    canTrigger: canTriggerQcFlowEvent,
    reset: resetQcFlow,
  } = qcFlowMachine;

  // 最近掃描的商品資訊（用於字幕顯示）
  const [videoSubtitle, setVideoSubtitle] = useState(null);

  // 影片上傳 Queue（提升到 Context 層級，確保跨訂單共享）
  const uploadQueue = useChunkedUploadQueue({
    signEndpoint: (entityId) =>
      `fulfillments/${entityId}/recording_chunks/sign`,
    finalizeEndpoint: (entityId, sessionId) =>
      `fulfillments/${entityId}/recording_sessions/${sessionId}/finalize`,
    onQueueOverflow: () => {
      Modal.error({
        title: i18n.t('operator.qc.video.queue_overflow_title', {
          defaultValue: 'Video upload paused',
        }),
        content: i18n.t('operator.qc.video.queue_overflow_content', {
          defaultValue:
            'Network looks unstable — upload queue is full. Please notify IT and stop recording until resolved.',
        }),
      });
    },
    onFinalizeFailed: ({ sessionId, missing }) => {
      console.error('[QC] Finalize failed', { sessionId, missing });
      Modal.error({
        title: i18n.t('operator.qc.video.finalize_failed_title', {
          defaultValue: 'Video upload failed',
        }),
        content: i18n.t('operator.qc.video.finalize_failed_content', {
          count: missing.length,
          defaultValue: `${missing.length} segments are missing — this video is incomplete. Please reset and re-QC this fulfillment.`,
        }),
      });
    },
  });

  // ✅ 同步 currentQcFulfillment 到 shipment context（用於列印完成後關閉訂單）
  useEffect(() => {
    qcShippingContext.setCurrentQcOrderRef(currentQcFulfillment);
  }, [currentQcFulfillment, qcShippingContext]);

  // 清除當前 QC 訂單
  const clearCurrentQcOrder = useCallback(() => {
    setCurrentQcFulfillment(null);
  }, []);

  // 重置到預設狀態（清除訂單、Parcels、物流客代等）
  // 若已有載入的訂單，會先 DELETE 後端 parcels，再清前端狀態。
  // 後端刪除失敗時保留當下狀態讓使用者重試，避免前後端不同步。
  const resetToDefaultState = useCallback(async () => {
    // 若已有載入的訂單，先刪除後端 parcels
    if (currentQcFulfillment?.id) {
      const result = await qcParcelContext.clearAllParcels(currentQcFulfillment);
      if (!result?.success) {
        message.error(i18n.t('operator.qc.list.reset_failed'));
        return { success: false };
      }
    }

    // 清除訂單
    setCurrentQcFulfillment(null);

    // 清除最近掃描的商品
    setVideoSubtitle(null);

    // 清除錯誤訊息
    setError(null);

    // 重置包裝狀態（清掉 clearAllParcels 留下的空箱子 placeholder）
    qcParcelContext.resetParcels();

    // 重置物流客代狀態
    qcShippingContext.resetShippingState();

    // 重置流程狀態
    resetQcFlow();

    return { success: true };
  }, [currentQcFulfillment, qcParcelContext, qcShippingContext, resetQcFlow]);

  // ✅ 統一的函數：從 parcels 重新計算 fulfillment items 的 shipped_quantity 和 serial_numbers
  // ⚠️ 必須在所有使用它的函數之前定義
  const recalculateOrderItemsFromParcels = useCallback((fulfillmentData, parcelsData) => {
    // 同 product_id+batch 但其他屬性 (inventory_type_id / expiration_date / ...) 不同的多 FI 情境:
    // 之前用 (product_id, batch) 聚合會把同一個 parcel_item 的數量廣播到每一個 match 的 FI,
    // 導致「掃一次,兩個 row 都 +1」。
    //
    // 修正:優先用 parcel_item.fulfillment_item_id 精確配對。
    //   - 主路徑 (scanItemByCode / batchScanItem / processFulfillmentData enrichment /
    //     ParcelItemsManagementModal spread) 都會帶 fulfillment_item_id。
    //   - 缺欄位的 parcel_item (理論上不該發生) 退回 (product_id, batch) 廣播當 safety net。
    const updatedItems = fulfillmentData.items.map(item => {
      let totalQuantity = 0;
      const allSerialNumbers = [];

      parcelsData.forEach((parcel) => {
        parcel.parcel_items?.forEach((parcelItem) => {
          const productAndBatchMatch =
            parcelItem.product_id === item.product.id &&
            isBatchMatch(parcelItem.batch, item.batch);
          if (!productAndBatchMatch) return;

          // 精確配對:有 fulfillment_item_id 時,只算到對應 FI 頭上。
          // 缺 fulfillment_item_id 時 (legacy parcel_item) 回到舊的廣播行為。
          const hasFulfillmentItemId = parcelItem.fulfillment_item_id != null;
          if (hasFulfillmentItemId && parcelItem.fulfillment_item_id !== item.id) {
            return;
          }

          totalQuantity += parcelItem.quantity || 0;
          if (parcelItem.serial_numbers && parcelItem.serial_numbers.length > 0) {
            allSerialNumbers.push(...parcelItem.serial_numbers);
          }
        });
      });

      // 計算 shipped_quantity
      let newShipped;
      const requiredSerialNumbersCount = item.product?.required_serial_numbers_count || 0;

      if (requiredSerialNumbersCount > 0) {
        newShipped = Math.floor(allSerialNumbers.length / requiredSerialNumbersCount);
      } else {
        newShipped = totalQuantity;
      }

      return {
        ...item,
        shipped_quantity: Math.max(0, newShipped),
        serial_numbers: allSerialNumbers
      };
    });

    return {
      ...fulfillmentData,
      items: updatedItems
    };
  }, []);

  // ✅ 統一的處理函數：處理從 API 取得的 fulfillment data 並更新所有相關狀態
  const processFulfillmentData = useCallback(async (fulfillmentData, code = null) => {
    // Use fulfillment name as code if not provided
    const scanCode = code || fulfillmentData.name;

    if (canTriggerQcFlowEvent(QCFlowEvents.SCAN_ORDER_BARCODE)) {
      triggerQcFlowEvent(QCFlowEvents.SCAN_ORDER_BARCODE, { code: scanCode });
    }

    // 驗證數據是否有效
    if (!fulfillmentData || !fulfillmentData.id || !fulfillmentData.items || fulfillmentData.items.length === 0) {
      const errorMessage = !fulfillmentData ? 'order_not_found' : 'order_has_no_items';
      setCurrentQcFulfillment(null);
      setError(errorMessage);
      triggerQcFlowEvent(QCFlowEvents.LOAD_FAILED, { code: scanCode, reason: errorMessage });
      return null;
    }


    // 載入已保存的 parcels（如果有的話）
    let enrichedParcels = [];
    let updatedFulfillmentData = fulfillmentData;
    let finalParcels = [];

    if (fulfillmentData.parcels && fulfillmentData.parcels.length > 0) {

      // 補充舊的 parcel_items 中缺失的 required_serial_numbers_count
      enrichedParcels = fulfillmentData.parcels.map((parcel) => {
        return {
          ...parcel,
          parcel_items: parcel.parcel_items.map((parcelItem) => {

            // 從 fulfillmentData.items 中找到對應的商品，取得完整的 product 資訊
            const item = fulfillmentData.items.find(
              i => i.product.id === parcelItem.product_id && isBatchMatch(i.batch, parcelItem.batch)
            );

            if (item && item.product) {
              return {
                ...parcelItem,
                // best-effort 指派：合單情境下同 SKU+batch 多 FI 時取第一筆，
                // 因為 parcel_items 表沒有 fulfillment_item_id 欄位，無法回溯原始 FI 歸屬。
                fulfillment_item_id: parcelItem.fulfillment_item_id || item.id,
                required_serial_numbers_count: item.product.required_serial_numbers_count || 0
              };
            }

            return parcelItem;
          })
        };
      });

      // 重建 order items 的 shipped_quantity 和 serial_numbers（從已保存的 parcels 計算）

      // 使用 enrichedParcels 重新計算
      const updatedResult = recalculateOrderItemsFromParcels(fulfillmentData, enrichedParcels);
      updatedFulfillmentData = updatedResult;

      setCurrentQcFulfillment(updatedFulfillmentData);

      // 使用補充後的 parcels，並在最後加上一個空的 parcel 供繼續掃描
      finalParcels = [...enrichedParcels, { parcel_items: [] }];
      qcParcelContext.setParcelsState(finalParcels, enrichedParcels.length);
    } else {
      // 沒有已保存的 parcel，初始化第一個空 parcel
      setCurrentQcFulfillment(fulfillmentData);
      finalParcels = [{ parcel_items: [] }];
      qcParcelContext.setParcelsState(finalParcels, 0);
    }


    // ShippingAccount 由後端 ShippingAccountResolver 自動選擇，前端不再處理

    // ✅ 檢查是否所有項目都已完成（決定觸發哪個事件）
    const allItemsCompleted = updatedFulfillmentData.items.every(item => {
      const shipped = item.shipped_quantity || 0;
      const required = item.quantity || 0;
      return shipped >= required;
    });

    if (allItemsCompleted) {
      // 所有項目都已完成，觸發 ORDER_LOADED_COMPLETED 事件，直接進入 AWAITING_RESCAN
      triggerQcFlowEvent(QCFlowEvents.ORDER_LOADED_COMPLETED, {
        code: scanCode,
        orderId: fulfillmentData.id,
        orderNumber: fulfillmentData.name,
      });
    } else {
      // 還有未完成的項目，觸發 ORDER_LOADED 事件，進入 SCANNING_ITEMS
      triggerQcFlowEvent(QCFlowEvents.ORDER_LOADED, {
        code: scanCode,
        orderId: fulfillmentData.id,
        orderNumber: fulfillmentData.name,
      });
    }

    return fulfillmentData;
  }, [canTriggerQcFlowEvent, triggerQcFlowEvent, qcParcelContext, qcShippingContext, recalculateOrderItemsFromParcels]);

  const fetchQcInfoByNumber = useCallback(async (number, { skipPlatformCheck = false } = {}) => {
    setLoading(true);
    setError(null);
    setErrorInterpolations(null);
    try {
      const url = `/fulfillments/qc_info?fulfillment_number=${encodeURIComponent(number)}`
        + (skipPlatformCheck ? '&skip_platform_check=true' : '');
      const response = await operatorApiFetch(url);
      const payload = await response.json();

      if (!response.ok) {
        const errorMessage = payload.message || payload.error || 'order_not_found';
        setCurrentQcFulfillment(null);
        setError(errorMessage);
        setErrorInterpolations(payload.interpolations || null);
        return null;
      }

      // 平台查單失敗：尚未進場，等待 operator 確認是否仍要進入 QC（確認後帶 skip 重打）
      if (payload.data?.platform_check_failed) {
        setPlatformCheckFailed({ retryFn: () => fetchQcInfoByNumber(number, { skipPlatformCheck: true }) });
        setLoading(false);
        return null;
      }

      // 包裝鎖定衝突：暫停流程，等待使用者確認接管
      if (payload.data?.packing_lock_conflict) {
        setPackingLockConflict({
          operatorName: payload.data.packing_operator_name,
          fulfillmentId: payload.data.id,
          retryFn: () => fetchQcInfoByNumber(number),
        });
        setLoading(false);
        return null;
      }

      return await processFulfillmentData(payload.data, number);
    } catch (err) {
      const fallbackMessage = err?.message || 'internal_server_error';
      setCurrentQcFulfillment(null);
      setError(fallbackMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [processFulfillmentData]);

  const loadFulfillmentById = useCallback(async (fulfillmentId, code = null, { skipPlatformCheck = false } = {}) => {
    setLoading(true);
    setError(null);
    setErrorInterpolations(null);
    setFulfillmentCandidates([]); // Clear candidates

    try {
      const url = `/fulfillments/${fulfillmentId}/qc_info`
        + (skipPlatformCheck ? '?skip_platform_check=true' : '');
      const response = await operatorApiFetch(url);
      const payload = await response.json();

      if (!response.ok) {
        const errorMessage = payload.message || payload.error || 'order_not_found';
        setCurrentQcFulfillment(null);
        setError(errorMessage);
        setErrorInterpolations(payload.interpolations || null);
        return null;
      }

      // 平台查單失敗：尚未進場，等待 operator 確認是否仍要進入 QC（確認後帶 skip 重打）
      if (payload.data?.platform_check_failed) {
        setPlatformCheckFailed({ retryFn: () => loadFulfillmentById(fulfillmentId, code, { skipPlatformCheck: true }) });
        setLoading(false);
        return null;
      }

      // 包裝鎖定衝突：暫停流程，等待使用者確認接管
      if (payload.data?.packing_lock_conflict) {
        setPackingLockConflict({
          operatorName: payload.data.packing_operator_name,
          fulfillmentId: payload.data.id,
          retryFn: () => loadFulfillmentById(fulfillmentId, code),
        });
        setLoading(false);
        return null;
      }

      return await processFulfillmentData(payload.data, code);
    } catch (err) {
      const fallbackMessage = err?.message || 'internal_server_error';
      setCurrentQcFulfillment(null);
      setError(fallbackMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [processFulfillmentData]);

  const selectFulfillment = useCallback(async (fulfillmentId) => {
    return await loadFulfillmentById(fulfillmentId);
  }, [loadFulfillmentById]);

  const scanOrderByCode = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();

    if (code.length === 0) {
      return null;
    }

    // ✅ 只處理正常的訂單掃描流程（載入訂單）
    if (!canTriggerQcFlowEvent(QCFlowEvents.SCAN_ORDER_BARCODE)) {
      return null;
    }

    setLoading(true);
    setError(null);
    setErrorInterpolations(null);
    setFulfillmentCandidates([]); // Reset candidates

    try {
      // First, search for fulfillment by code (scan)
      const scanResponse = await operatorApiFetch(`/fulfillments/scan?code=${encodeURIComponent(code)}`, {
        method: 'POST',
      });

      let scanResult = await scanResponse.json();

      if (!scanResponse.ok) {
        // Fallback: try orders qc_scan if fulfillment scan fails (migration support?)
        // For now, assume we strictly use fulfillment scan as per requirement
        const errorMessage = scanResult.message || scanResult.error || 'order_not_found';
        setCurrentQcFulfillment(null);
        setError(errorMessage);
        return null;
      }

      // Handle multiple candidates
      if (scanResult.data.selection_required) {
        setFulfillmentCandidates(scanResult.data.candidates);
        return { selectionRequired: true };
      }

      const fulfillmentId = scanResult.data.id;
      return await loadFulfillmentById(fulfillmentId, code);

    } catch (err) {
      const fallbackMessage = err?.message || 'internal_server_error';
      setCurrentQcFulfillment(null);
      setError(fallbackMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [canTriggerQcFlowEvent, loadFulfillmentById]);

  // 掃描條碼以觸發列印（專門用於 AWAITING_RESCAN 狀態）
  const scanBarcodeForPrinting = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();

    if (code.length === 0) {
      return null;
    }

    setError(null);

    if (!currentQcFulfillment) {
      message.warning('無當前出貨單資訊');
      return null;
    }

    // 驗證掃描的條碼是否與當前出貨單匹配
    // Fulfillment name usually is the barcode or code
    // Also check associated orders
    const confirmBarcodes = [
      SPECIAL_BARCODES.CONFIRM_PRINT.code,
      ...SPECIAL_BARCODES.CONFIRM_PRINT.alternativeCodes
    ];

    const isValidBarcode = (
      confirmBarcodes.includes(code) ||
      currentQcFulfillment.name === code ||
      currentQcFulfillment.number === code ||
      (currentQcFulfillment.orders && currentQcFulfillment.orders.some(o => o.name === code))
    );


    if (!isValidBarcode) {
      message.warning('請掃描當前出貨單或訂單的條碼以進行列印');
      return null;
    }


    // ✅ 1. 檢查當前 parcel 是否有商品，如果有則先送出
    const currentParcel = qcParcelContext.parcels[qcParcelContext.currentParcelIndex];
    if (currentParcel && currentParcel.parcel_items && currentParcel.parcel_items.length > 0) {

      try {
        const parcelResult = await qcParcelContext.performParcel(currentQcFulfillment, qcParcelContext.currentParcelIndex);

        if (!parcelResult.success) {
          console.error('[QcContext][scanBarcodeForPrinting] ✗ Failed to pack current items:', parcelResult.error);
          message.error(`無法送出當前包裝: ${parcelResult.error}`);
          return null;
        }
        // 列印是 QC 流程結尾,沒有下一個 parcel 要掃 — 不該再 addNewParcel,
        // 否則 COMPLETED 狀態 UI 上會多一個空箱。
      } catch (err) {
        console.error('[QcContext][scanBarcodeForPrinting] ✗ Exception during parcel:', err);
        message.error('送出包裝時發生錯誤');
        return null;
      }
    }

    // ✅ 2. 空箱防護：計算有品項的 parcel 數，若為 0 則阻擋並退回 SCANNING_ITEMS
    const quantity = qcParcelContext.parcels.filter(p => p.parcel_items && p.parcel_items.length > 0).length;
    if (quantity === 0) {
      message.warning(i18n.t('operator.qc.list.print_blocked_empty_parcels'));
      if (canTriggerQcFlowEvent(QCFlowEvents.ITEM_ADJUSTMENT)) {
        triggerQcFlowEvent(QCFlowEvents.ITEM_ADJUSTMENT);
      }
      return null;
    }

    // ✅ 3. 觸發 RESCAN_ORDER_FOR_SHIPPING 事件，進入 PRINTING 狀態
    if (canTriggerQcFlowEvent(QCFlowEvents.RESCAN_ORDER_FOR_SHIPPING)) {
      triggerQcFlowEvent(QCFlowEvents.RESCAN_ORDER_FOR_SHIPPING, { code });
    }

    // 呼叫 print_label API
    try {
      setLoading(true);
      const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/print_label`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity }),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.message || result.error || 'shipment_failed';
        const interpolations = result.interpolations || {};
        console.error('[QcContext][scanBarcodeForPrinting] ✗ Print API failed:', errorMessage, interpolations);
        setError(errorMessage);
        setErrorInterpolations(interpolations);
        message.error(
          `${i18n.t('operator.qc.print_label.failed')}: ${i18n.t(`common.error.${errorMessage}`, {
            ...interpolations,
            defaultValue: errorMessage,
          })}`
        );

        // 觸發失敗事件，回到 AWAITING_RESCAN
        triggerQcFlowEvent(QCFlowEvents.PRINT_FAILED_OR_CANCELED, { error: errorMessage });
        return null;
      }

      // 儲存 job_id，準備監聯
      const jobId = result.data?.job_id;
      const skipped = result.data?.skipped;

      if (jobId) {
        qcShippingContext.setPrintJobId(jobId);
        message.info(i18n.t('operator.qc.print_label.preparing'));
      } else if (skipped) {
        // 不需要印單的情況(Custom 未人工上傳託運單 / None 無追蹤號碼 / 搬家舊資料)→ 跳過標籤列印,直接完成 QC。
        // attempt_leave_qc_page log 不在這裡發 — 改由 Index.jsx 的 useBlocker/beforeunload 攔截處
        // 處理「使用者嘗試離開」的紀錄。
        operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/complete_qc`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).then(async (completeResponse) => {
          if (completeResponse.ok) {
            console.log('[QcContext] ✓ Custom fulfillment QC completed (skipped print)');
            message.success(i18n.t('operator.qc.print_label.order_completed'));
          } else {
            const result = await completeResponse.json().catch(() => ({}));
            const errorMessage = result.message || result.error || 'complete_order_failed';
            const interpolations = result.interpolations || {};
            console.error('[QcContext] ✗ complete_qc API failed:', errorMessage, interpolations);
            message.error(`${i18n.t('operator.qc.print_label.order_complete_failed')}: ${i18n.t(`common.error.${errorMessage}`, { ...interpolations, defaultValue: errorMessage })}`);
            // 同 QcProvider.handleOrderComplete:寫到持久 error channel 讓 Alert 顯示
            setError(errorMessage);
            setErrorInterpolations(interpolations);
          }
        }).catch(err => {
          console.error('[QcContext] complete_qc failed:', err);
          const errorMessage = err?.message || 'internal_server_error';
          message.error(`${i18n.t('operator.qc.print_label.order_complete_failed')}: ${errorMessage}`);
          setError(errorMessage);
          setErrorInterpolations(null);
        });
        triggerQcFlowEvent(QCFlowEvents.PRINT_SUCCESS);
      } else {
        console.error('[QcContext][scanBarcodeForPrinting] ✗ No job_id in response');
        triggerQcFlowEvent(QCFlowEvents.PRINT_FAILED_OR_CANCELED, { error: 'no_job_id' });
      }

      return result.data;
    } catch (err) {
      console.error('[QcContext][scanBarcodeForPrinting] ✗ Exception:', err);
      const errorMessage = err?.message || 'internal_server_error';
      setError(errorMessage);
      message.error(`${i18n.t('operator.qc.print_label.failed')}: ${i18n.t(`common.error.${errorMessage}`, { defaultValue: errorMessage })}`);
      triggerQcFlowEvent(QCFlowEvents.PRINT_FAILED_OR_CANCELED, { error: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentQcFulfillment, qcParcelContext, qcShippingContext, canTriggerQcFlowEvent, triggerQcFlowEvent]);

  // auto-pack 失敗時的統一 UX surfacing。線上觀察到 backend 因 duplicate_serial_number 等
  // 原因回 422,以前只 console.error 然後 silently 卡在 SCANNING_ITEMS,user 無從得知 →
  // user 後續刷出貨單號又被 routing 到 scanItemByCode 拿到 "找不到商品" 誤導訊息。
  // 現在改為:setError (頁面上方 Alert 顯示) + message.error toast。
  const surfaceAutoPackError = useCallback((packResult) => {
    if (!packResult || packResult.success !== false || !packResult.error) return;
    const interpolations = packResult.interpolations || {};
    setError(packResult.error);
    setErrorInterpolations(interpolations);
    message.error(
      `${i18n.t('operator.qc.list.errors.auto_pack_failed', { defaultValue: '自動裝箱失敗' })}: ${i18n.t(`common.error.${packResult.error}`, {
        ...interpolations,
        defaultValue: packResult.error,
      })}`
    );
  }, []);

  const scanItemByCode = useCallback(async (rawCode, serialNumbers) => {
    const code = String(rawCode || '').trim();

    if (code.length === 0) {
      return null;
    }

    if (!currentQcFulfillment || !currentQcFulfillment.id || !currentQcFulfillment.items) {
      return null;
    }

    setError(null);

    // 在訂單項目中尋找符合的商品
    let matchedItem = null;
    let candidates = [];
    let scanQuantity = 1; // 掃描一次代表的數量（中/大單位時會乘以入數）

    // 1. 比對 product barcode
    candidates = currentQcFulfillment.items.filter(item => item.product?.barcode === code);

    // 2. 比對 product secondary_barcodes
    if (candidates.length === 0) {
      candidates = currentQcFulfillment.items.filter(item => {
        const secondaryBarcodes = item.product?.secondary_barcodes;
        return Array.isArray(secondaryBarcodes) && secondaryBarcodes.includes(code);
      });
    }

    // 3. 比對中單位條碼（mid_unit_barcode）
    if (candidates.length === 0) {
      candidates = currentQcFulfillment.items.filter(item =>
        item.product?.mid_unit_barcode && item.product.mid_unit_barcode === code &&
        item.product.small_quantity_per_mid_unit > 0
      );
      if (candidates.length > 0) {
        scanQuantity = candidates[0].product.small_quantity_per_mid_unit;
      }
    }

    // 4. 比對大單位條碼（large_unit_barcode）
    if (candidates.length === 0) {
      candidates = currentQcFulfillment.items.filter(item =>
        item.product?.large_unit_barcode && item.product.large_unit_barcode === code &&
        item.product.small_quantity_per_large_unit > 0
      );
      if (candidates.length > 0) {
        scanQuantity = candidates[0].product.small_quantity_per_large_unit;
      }
    }

    // 5. 比對 product sku
    if (candidates.length === 0) {
      candidates = currentQcFulfillment.items.filter(item => item.product?.sku === code);
    }

    // 6. 比對 order_item batch
    if (candidates.length === 0) {
      candidates = currentQcFulfillment.items.filter(item => item.batch && item.batch === code);
    }

    // 從候選項目中，優先選擇尚未完成的項目
    if (candidates.length > 0) {
      matchedItem = candidates.find(item => {
        const shipped = item.shipped_quantity || 0;
        return shipped < item.quantity;
      });

      if (!matchedItem) {
        matchedItem = candidates[0];
      }
    }

    if (!matchedItem) {
      setError('operator.qc.list.errors.product_not_found');
      return null;
    }

    // 檢查是否需要序號（required_serial_numbers_count > 0 即為需要）
    const requiredSerialNumbersCount = matchedItem.product?.required_serial_numbers_count || 0;
    const needSerialNumber = requiredSerialNumbersCount > 0;

    // 如果需要序號但沒有提供序號，返回特殊狀態要求輸入序號
    // 把 scanQuantity 一起回傳，modal 才能算出 scanQuantity * required_per_unit
    if (needSerialNumber && (!serialNumbers || serialNumbers.length === 0)) {
      return {
        needSerialNumber: true,
        matchedItem,
        scanQuantity,
        requiredSerialCount: scanQuantity * requiredSerialNumbersCount
      };
    }

    // 序號流程的最終單位數由 serialNumbers.length 決定（支援箱標一次刷多筆）。
    // 必須是 required_per_unit 的整數倍，否則就是貼錯標 / scanner 切錯。
    let unitsToAdd = scanQuantity;
    if (needSerialNumber && serialNumbers) {
      if (
        serialNumbers.length === 0 ||
        serialNumbers.length % requiredSerialNumbersCount !== 0
      ) {
        setError('operator.qc.list.errors.serial_count_mismatch');
        return null;
      }
      unitsToAdd = serialNumbers.length / requiredSerialNumbersCount;
    }

    // 合單情境：兩張訂單同 SKU 同 batch 會出現兩個 FI，可出貨量是兩 FI 容量和。
    // 容量與已出量都按 [product_id, batch] 聚合，與 backend pack 的量檢一致。
    const combinedCapacity = currentQcFulfillment.items.reduce((sum, fi) => {
      if (fi.product?.id === matchedItem.product.id && isBatchMatch(fi.batch, matchedItem.batch)) {
        return sum + (fi.quantity || 0);
      }
      return sum;
    }, 0);

    let currentShippedFromParcels = 0;
    const currentSerialNumbersFromParcels = [];

    qcParcelContext.parcels.forEach(parcel => {
      parcel.parcel_items?.forEach(parcelItem => {
        if (parcelItem.product_id === matchedItem.product.id &&
          isBatchMatch(parcelItem.batch, matchedItem.batch)) {
          currentShippedFromParcels += parcelItem.quantity || 0;
          if (parcelItem.serial_numbers && parcelItem.serial_numbers.length > 0) {
            currentSerialNumbersFromParcels.push(...parcelItem.serial_numbers);
          }
        }
      });
    });

    // 計算新的已刷驗數量
    const newShipped = currentShippedFromParcels + unitsToAdd;

    // 檢查是否超過訂單數量（以合計容量為上限）
    if (newShipped > combinedCapacity) {
      setError('operator.qc.list.errors.quantity_exceeded');
      return null;
    }

    // 更新最近掃描的商品資訊（用於字幕顯示）
    setVideoSubtitle({
      text: `${matchedItem.product.sku}  ${matchedItem.product.name}`,
      timestamp: Date.now()
    });

    // ✅ 只更新 parcel，不更新 order items（讓 useEffect 自動同步）
    const updatedParcels = [...qcParcelContext.parcels];
    const currentParcel = updatedParcels[qcParcelContext.currentParcelIndex];

    if (!currentParcel) {
      console.error('[QcContext][scanItemByCode] ✗ No current parcel found!');
      return { scannedItemId: matchedItem.id };
    }

    // 合併由 fulfillment_item_id 決定：同一個 FI 才併入既有 parcel_item，
    // 不同 FI 即使 SKU+batch 相同也分開 — 才能讓 audit / FISN 連到正確的 FI。
    const existingItemIndex = currentParcel.parcel_items.findIndex(
      item => item.fulfillment_item_id === matchedItem.id
    );

    if (existingItemIndex >= 0) {
      // 如果已存在，增加數量和序號
      currentParcel.parcel_items[existingItemIndex].quantity += unitsToAdd;
      if (serialNumbers && serialNumbers.length > 0) {
        const existingSerials = currentParcel.parcel_items[existingItemIndex].serial_numbers || [];
        currentParcel.parcel_items[existingItemIndex].serial_numbers = [...existingSerials, ...serialNumbers];
      }
    } else {
      // 如果不存在，新增一筆
      const parcelItem = {
        fulfillment_item_id: matchedItem.id,
        product_id: matchedItem.product.id,
        product_sku: matchedItem.product.sku,
        product_name: matchedItem.product.name,
        quantity: unitsToAdd,
        batch: matchedItem.batch || null,
        serial_numbers: serialNumbers && serialNumbers.length > 0 ? [...serialNumbers] : [],
        required_serial_numbers_count: matchedItem.product?.required_serial_numbers_count || 0,
      };

      currentParcel.parcel_items.push(parcelItem);
    }

    // 更新 parcel context（會觸發 useEffect 自動重新計算 order items）
    qcParcelContext.setParcelsState(updatedParcels, qcParcelContext.currentParcelIndex);

    // ✅ 直接呼叫 auto-pack 檢查，傳入本地最新數據，避免 useEffect 重複觸發
    const updatedFulfillment = recalculateOrderItemsFromParcels(currentQcFulfillment, updatedParcels);
    const packResult = await qcParcelContext.checkAndAutoPackIfComplete(updatedFulfillment, updatedParcels);
    surfaceAutoPackError(packResult);

    return { scannedItemId: matchedItem.id };
  }, [currentQcFulfillment, qcParcelContext, recalculateOrderItemsFromParcels, surfaceAutoPackError]);

  const batchScanItem = useCallback(async (item, quantity, serialNumbers) => {

    if (!item || !currentQcFulfillment || quantity <= 0) {
      return null;
    }

    // 合單情境：兩張訂單同 SKU 同 batch 會出現兩個 FI，可出貨量是兩 FI 容量和。
    const combinedCapacity = currentQcFulfillment.items.reduce((sum, fi) => {
      if (fi.product?.id === item.product.id && isBatchMatch(fi.batch, item.batch)) {
        return sum + (fi.quantity || 0);
      }
      return sum;
    }, 0);

    let currentShippedFromParcels = 0;
    const currentSerialNumbersFromParcels = [];
    const requiredSerialNumbersCount = item.product?.required_serial_numbers_count || 0;
    const needSerialNumber = requiredSerialNumbersCount > 0;

    qcParcelContext.parcels.forEach(parcel => {
      parcel.parcel_items?.forEach(parcelItem => {
        if (parcelItem.product_id === item.product.id &&
          isBatchMatch(parcelItem.batch, item.batch)) {
          currentShippedFromParcels += parcelItem.quantity || 0;
          if (parcelItem.serial_numbers && parcelItem.serial_numbers.length > 0) {
            currentSerialNumbersFromParcels.push(...parcelItem.serial_numbers);
          }
        }
      });
    });

    // 計算新的已刷驗數量
    const newShipped = needSerialNumber
      ? Math.floor((currentSerialNumbersFromParcels.length + (serialNumbers?.length || 0)) / requiredSerialNumbersCount)
      : currentShippedFromParcels + quantity;

    // 檢查是否超過訂單數量（以合計容量為上限）
    if (newShipped > combinedCapacity) {
      setError('operator.qc.list.errors.quantity_exceeded');
      return null;
    }

    setError(null);

    // 更新最近掃描的商品資訊（用於字幕顯示）
    setVideoSubtitle({
      text: `${item.product.sku}  ${item.product.name}`,
      timestamp: Date.now()
    });

    // ✅ 只更新 parcel，不更新 order items（讓 useEffect 自動同步）
    const updatedParcels = [...qcParcelContext.parcels];
    const currentParcel = updatedParcels[qcParcelContext.currentParcelIndex];

    if (!currentParcel) {
      console.error('[QcContext][batchScanItem] ✗ No current parcel found!');
      return { itemId: item.id };
    }

    // 合併由 fulfillment_item_id 決定（與 scanItemByCode 一致）
    const existingItemIndex = currentParcel.parcel_items.findIndex(
      parcelItem => parcelItem.fulfillment_item_id === item.id
    );

    if (existingItemIndex >= 0) {
      // 如果已存在，增加數量和序號
      currentParcel.parcel_items[existingItemIndex].quantity += quantity;
      if (serialNumbers && serialNumbers.length > 0) {
        const existingSerials = currentParcel.parcel_items[existingItemIndex].serial_numbers || [];
        currentParcel.parcel_items[existingItemIndex].serial_numbers = [...existingSerials, ...serialNumbers];
      }
    } else {
      // 如果不存在，新增一筆
      const parcelItem = {
        fulfillment_item_id: item.id,
        product_id: item.product.id,
        product_sku: item.product.sku,
        product_name: item.product.name,
        quantity,
        batch: item.batch || null,
        serial_numbers: serialNumbers && serialNumbers.length > 0 ? [...serialNumbers] : [],
        required_serial_numbers_count: item.product?.required_serial_numbers_count || 0,
      };

      currentParcel.parcel_items.push(parcelItem);
    }

    // 更新 parcel context（會觸發 useEffect 自動重新計算 order items）
    qcParcelContext.setParcelsState(updatedParcels, qcParcelContext.currentParcelIndex);

    // ✅ 直接呼叫 auto-pack 檢查，傳入本地最新數據，避免 useEffect 重複觸發
    const updatedFulfillment = recalculateOrderItemsFromParcels(currentQcFulfillment, updatedParcels);
    const packResult = await qcParcelContext.checkAndAutoPackIfComplete(updatedFulfillment, updatedParcels);
    surfaceAutoPackError(packResult);

    return { itemId: item.id };
  }, [currentQcFulfillment, qcParcelContext, recalculateOrderItemsFromParcels, surfaceAutoPackError]);

  const removeSerialNumber = useCallback((item, serialNumber, index) => {

    if (!item || !currentQcFulfillment) {
      return null;
    }

    setError(null);

    // ✅ 只更新 parcels，不更新 order items（讓 useEffect 自動同步）
    const updatedParcels = [...qcParcelContext.parcels];
    let removed = false;

    // 遍歷所有 parcels，找到並移除該序號
    updatedParcels.forEach(parcel => {
      parcel.parcel_items?.forEach(parcelItem => {
        if (parcelItem.product_id === item.product.id &&
          isBatchMatch(parcelItem.batch, item.batch) &&
          parcelItem.serial_numbers) {
          const serialIndex = parcelItem.serial_numbers.indexOf(serialNumber);
          if (serialIndex >= 0) {
            // 移除序號
            parcelItem.serial_numbers.splice(serialIndex, 1);
            removed = true;

            // 重新計算數量（如果需要序號）
            const requiredSerialNumbersCount = item.product?.required_serial_numbers_count || 1;
            parcelItem.quantity = Math.floor(parcelItem.serial_numbers.length / requiredSerialNumbersCount);
          }
        }
      });

      // 移除空的 parcel_items
      parcel.parcel_items = parcel.parcel_items?.filter(pi => pi.quantity > 0) || [];
    });

    if (!removed) {
      return null;
    }

    // 更新 parcel context（會觸發 useEffect 自動重新計算 order items）
    qcParcelContext.setParcelsState(updatedParcels, qcParcelContext.currentParcelIndex);

    return { success: true };
  }, [currentQcFulfillment, qcParcelContext]);

  // ✅ 監聽 parcels 的變化，自動同步更新 order items（確保右邊的任何變更都同步到左側）
  useEffect(() => {
    // 確保有訂單和商品
    if (!currentQcFulfillment || !currentQcFulfillment.items || currentQcFulfillment.items.length === 0) {
      return;
    }

    // 確保有 parcel
    if (!parcels || parcels.length === 0) {
      return;
    }

    const updatedFulfillment = recalculateOrderItemsFromParcels(currentQcFulfillment, parcels);

    // 只有在 shipped_quantity 或 serial_numbers 真的改變時才更新
    const hasChanged = currentQcFulfillment.items.some((item, index) => {
      const updatedItem = updatedFulfillment.items[index];
      return item.shipped_quantity !== updatedItem.shipped_quantity ||
        JSON.stringify(item.serial_numbers) !== JSON.stringify(updatedItem.serial_numbers);
    });

    if (hasChanged) {
      setCurrentQcFulfillment(updatedFulfillment);
    }
  }, [parcels, currentQcFulfillment, recalculateOrderItemsFromParcels]); // 監聽 parcels 和 currentQcFulfillment 的變化

  // ✅ auto-pack 檢查已移至 scanItemByCode / batchScanItem 中直接呼叫
  // 不再透過 useEffect 監聽 parcels/currentQcFulfillment 變化來觸發
  // 這樣可以確保每次掃描只觸發一次 pack API，避免重複呼叫

  // 修正 updateParcelItems：需要同步更新 currentQcFulfillment
  const updateParcelItemsWrapper = useCallback(async (parcelIndex, updatedItems) => {
    const result = await qcParcelContext.updateParcelItems(currentQcFulfillment, parcelIndex, updatedItems);

    if (result.success && result.updatedParcels) {
      // 使用統一的函數重新計算 items
      const updatedFulfillment = recalculateOrderItemsFromParcels(currentQcFulfillment, result.updatedParcels);
      setCurrentQcFulfillment(updatedFulfillment);

      // 編輯 parcel_items 後若所有 items 都湊到 complete,直接 trigger ALL_ITEMS_COMPLETED
      // 把 state 推到 AWAITING_RESCAN。不能走 checkAndAutoPackIfComplete:那條路徑會卡在
      // currentParcel.parcel_id 的 guard (updateParcelItems 內部已 POST /pack 完了),
      // 結果就是 state 卡在 SCANNING_ITEMS — 線上 user 踩到的 stuck bug 的另一條觸發路徑。
      const allItemsCompleted = updatedFulfillment.items.length > 0 &&
        updatedFulfillment.items.every(item =>
          (item.shipped_quantity || 0) >= (item.quantity || 0)
        );
      if (allItemsCompleted && canTriggerQcFlowEvent(QCFlowEvents.ALL_ITEMS_COMPLETED)) {
        triggerQcFlowEvent(QCFlowEvents.ALL_ITEMS_COMPLETED, {
          orderId: updatedFulfillment.id,
          orderNumber: updatedFulfillment.name,
        });
      }
    }

    return result;
  }, [currentQcFulfillment, qcParcelContext, recalculateOrderItemsFromParcels, canTriggerQcFlowEvent, triggerQcFlowEvent]);

  // 清除所有 parcels 的 wrapper：需要同步更新 currentQcFulfillment
  const clearAllParcelsWrapper = useCallback(async (currentQcFulfillment) => {
    const result = await qcParcelContext.clearAllParcels(currentQcFulfillment);

    // 根據實際剩餘的 parcels 重新計算 items（部分刪除失敗時保留未刪除的）
    const remaining = result.remainingParcels || [{ parcel_items: [] }];
    const updatedFulfillment = recalculateOrderItemsFromParcels(currentQcFulfillment, remaining);
    setCurrentQcFulfillment(updatedFulfillment);

    // 清除後若已無任何非空 parcel，從 AWAITING_RESCAN 退回 SCANNING_ITEMS
    // 避免空箱掃描訂單號觸發空出貨
    const hasAnyItems = remaining.some(p => p.parcel_items && p.parcel_items.length > 0);
    if (!hasAnyItems && canTriggerQcFlowEvent(QCFlowEvents.ITEM_ADJUSTMENT)) {
      triggerQcFlowEvent(QCFlowEvents.ITEM_ADJUSTMENT);
    }

    return result;
  }, [qcParcelContext, recalculateOrderItemsFromParcels, canTriggerQcFlowEvent, triggerQcFlowEvent]);

  // 確認接管包裝鎖定：call takeover_packing 搶鎖，再重新載入
  const confirmPackingTakeover = useCallback(async () => {
    if (!packingLockConflict?.retryFn || !packingLockConflict?.fulfillmentId) return;
    const { fulfillmentId, retryFn } = packingLockConflict;
    setPackingLockConflict(null);

    try {
      await operatorApiFetch(`/fulfillments/${fulfillmentId}/takeover_packing`, { method: 'POST' });
    } catch (err) {
      console.error('[QcContext] takeover_packing failed:', err);
    }

    await retryFn();
  }, [packingLockConflict]);

  // 取消接管：重置 QC 流程回到等待掃描狀態
  const cancelPackingTakeover = useCallback(() => {
    setPackingLockConflict(null);
    resetQcFlow();
  }, [resetQcFlow]);

  // 平台查單失敗後 operator 確認仍要進入 QC：帶 skip_platform_check 重打 qc_info
  const confirmProceedWithoutPlatformCheck = useCallback(async () => {
    if (!platformCheckFailed?.retryFn) return;
    const { retryFn } = platformCheckFailed;
    setPlatformCheckFailed(null);
    await retryFn();
  }, [platformCheckFailed]);

  // 取消進入：重置 QC 流程回到等待掃描狀態
  const cancelProceedWithoutPlatformCheck = useCallback(() => {
    setPlatformCheckFailed(null);
    resetQcFlow();
  }, [resetQcFlow]);

  const value = {
    currentQcFulfillment,
    loading,
    error,
    errorInterpolations,
    fulfillmentCandidates,
    selectFulfillment,
    // Operator context (delegated)
    qcOperator: qcOperatorContext.qcOperator,
    parcelOperator: qcOperatorContext.parcelOperator,
    operatorsVerified: qcOperatorContext.operatorsVerified,
    verifyOperators: qcOperatorContext.verifyOperators,
    setQcOperator: qcOperatorContext.setQcOperator,
    setParcelOperator: qcOperatorContext.setParcelOperator,
    clearOperators: qcOperatorContext.clearOperators,
    operatorLoading: qcOperatorContext.loading,
    operatorError: qcOperatorContext.error,
    // Order management
    clearCurrentQcOrder,
    resetToDefaultState,
    scanOrderByCode,
    fetchQcInfoByNumber, // 新增：提供給外部直接透過編號獲取 QC 資訊
    scanBarcodeForPrinting,
    scanItemByCode,
    batchScanItem,
    removeSerialNumber,
    // Flow state machine
    qcFlowState,
    qcFlowContext,
    qcFlowPreviousState,
    qcFlowLastEvent,
    qcFlowLabel,
    triggerQcFlowEvent,
    canTriggerQcFlowEvent,
    resetQcFlow,
    QCFlowStates,
    QCFlowEvents,
    // Parcel context (delegated)
    parcels: qcParcelContext.parcels,
    currentParcelIndex: qcParcelContext.currentParcelIndex,
    addNewParcel: qcParcelContext.addNewParcel,
    switchToParcel: qcParcelContext.switchToParcel,
    performParcel: qcParcelContext.performParcel,
    updateParcelItems: updateParcelItemsWrapper,
    clearAllParcels: clearAllParcelsWrapper,
    setVideoSplitter: qcParcelContext.setVideoSplitter,
    // Last scanned product (for subtitle display)
    videoSubtitle,
    // Video upload queue (shared across orders)
    uploadQueue,
    // 包裝鎖定衝突
    packingLockConflict,
    confirmPackingTakeover,
    cancelPackingTakeover,
    // 平台訂單取消查詢失敗
    platformCheckFailed,
    confirmProceedWithoutPlatformCheck,
    cancelProceedWithoutPlatformCheck,
  };

  return (
    <QcContext.Provider value={value}>
      {children}
    </QcContext.Provider>
  );
};

// ✅ 主 Provider：包裹所有子 Context
export const QcProvider = ({ children }) => {
  // ✅ 創建唯一的狀態機實例（傳遞給所有子 Context）
  const qcFlowMachine = useQcFlowMachine();
  const { state: qcFlowState, trigger: triggerQcFlowEvent } = qcFlowMachine;

  // 列印失敗錯誤（從 QcShipmentContext 回傳，同步到 QcContextInner）
  const [printError, setPrintError] = useState(null);
  const handlePrintError = useCallback((errorMsg, interpolations = null) => {
    if (typeof errorMsg === 'object' && errorMsg !== null) {
      setPrintError(errorMsg);
    } else {
      setPrintError({ code: errorMsg, interpolations });
    }
  }, []);

  // 完成訂單的回調函數（傳遞給 QcShipmentProvider）
  // 前端收到 PRINT_SUCCESS 後呼叫,通知後端執行 CompletionService。
  // attempt_leave_qc_page log 改由 Index.jsx 的 useBlocker/beforeunload 攔截處發送 —
  // 那才是真的「使用者嘗試離開」。在這裡發是歷史遺物,名稱也描述不準(沒人在離開)。
  // confirmWithoutLabel：列印失敗後操作員在彈窗確認「仍要結單」，帶 confirm_without_label=true
  // 讓後端跳過 SHIPMENT_LABEL_NOT_GENERATED 檢查強制結單（無單號出貨）。
  const handleOrderComplete = useCallback(async (fulfillmentId, { confirmWithoutLabel = false } = {}) => {
    try {
      const completeResponse = await operatorApiFetch(`/fulfillments/${fulfillmentId}/complete_qc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_without_label: confirmWithoutLabel }),
      });

      const result = await completeResponse.json();

      if (!completeResponse.ok) {
        const errorMessage = result.message || result.error || 'complete_order_failed';
        const interpolations = result.interpolations || {};
        console.error('[QcProvider] ✗ complete_qc API failed:', errorMessage, interpolations);
        message.error(`${i18n.t('operator.qc.print_label.order_complete_failed')}: ${i18n.t(`common.error.${errorMessage}`, { ...interpolations, defaultValue: errorMessage })}`);
        // 同步寫到持久 error channel，讓 QcContextInner 在畫面上以 Alert 持久顯示
        setPrintError({ code: errorMessage, interpolations });
        return { success: false, error: errorMessage };
      }

      console.log('[QcProvider] ✓ Fulfillment QC completed');
      message.success(i18n.t('operator.qc.print_label.order_completed'));
      return { success: true };
    } catch (err) {
      console.error('[QcProvider] ✗ Exception:', err);
      const errorMessage = err?.message || 'internal_server_error';
      message.error(`${i18n.t('operator.qc.print_label.order_complete_failed')}: ${i18n.t(`common.error.${errorMessage}`, { defaultValue: errorMessage })}`);
      setPrintError({ code: errorMessage, interpolations: null });
      return { success: false, error: errorMessage };
    }
  }, []);

  return (
    <QcOperatorProvider>
      <QcParcelProvider
        qcFlowState={qcFlowState}
        triggerQcFlowEvent={triggerQcFlowEvent}
      >
        <QcShipmentProvider
          triggerQcFlowEvent={triggerQcFlowEvent}
          onOrderComplete={handleOrderComplete}
          onPrintError={handlePrintError}
        >
          <QcContextInner qcFlowMachine={qcFlowMachine} externalPrintError={printError} clearExternalPrintError={() => setPrintError(null)}>
            {children}
          </QcContextInner>
        </QcShipmentProvider>
      </QcParcelProvider>
    </QcOperatorProvider>
  );
};

export const useQc = () => {
  const context = useContext(QcContext);
  if (!context) {
    throw new Error('useQc must be used within a QcProvider');
  }
  return context;
};

export default QcContext;
